/**
 * Seitenzustände der Einstellungen vor dem Formular. Ein Ladefehler ist nie „Apartment wählen“: fehlt
 * die Objektliste, heißt das so; ohne Netz pausiert die Abfrage und wartet sichtbar statt endlos zu
 * „laden“ (gotchas-aspnet §6.168).
 */
import { EmptyState, SurfaceLoadError, SurfaceLoading } from '@/shared/components';

export type ConfigurationPageStateKind =
  | 'empty'
  | 'scopeError'
  | 'error'
  | 'offline'
  | 'loading'
  | 'ready';

interface Query {
  data?: unknown;
  error: unknown;
  fetchStatus: string;
  isLoading: boolean;
}

export function pageState(input: {
  apartmentId?: string;
  scope: { isError: boolean; groups: unknown[] };
  configuration: Query;
  profiles: Query;
  activeProfile: Query;
}): ConfigurationPageStateKind {
  const { configuration, profiles } = input;
  if (!input.apartmentId) {
    return input.scope.isError && input.scope.groups.length === 0 ? 'scopeError' : 'empty';
  }
  if ((!configuration.data && configuration.error) || (!profiles.data && profiles.error)) {
    return 'error';
  }
  if (!configuration.data && configuration.fetchStatus === 'paused') return 'offline';
  if (!configuration.data || !profiles.data || input.activeProfile.isLoading) return 'loading';
  return 'ready';
}

const card = 'bg-card border-hairline rounded-tile shadow-float overflow-hidden border';

export function ConfigurationPageState({
  state,
  reason,
  onRetry,
  retrying,
}: {
  state: ConfigurationPageStateKind;
  reason: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <section className={card}>
      {state === 'empty' && (
        <EmptyState
          icon="Building"
          title="Apartment wählen"
          description="Oben rechts ein Apartment der aktuellen Objektwahl wählen – die Einstellungen gelten je Apartment."
        />
      )}
      {state === 'scopeError' && (
        <SurfaceLoadError
          message="Die Objekte konnten nicht geladen werden – deshalb lässt sich kein Apartment wählen."
          onRetry={onRetry}
          retrying={retrying}
        />
      )}
      {state === 'error' && (
        <SurfaceLoadError message={reason} onRetry={onRetry} retrying={retrying} />
      )}
      {state === 'offline' && (
        <p role="status" className="text-ink-soft p-5 text-sm">
          Keine Verbindung – die Einstellungen laden, sobald das Netz zurück ist.
        </p>
      )}
      {(state === 'loading' || state === 'ready') && (
        <SurfaceLoading label="Einstellungen werden geladen" rows={4} />
      )}
    </section>
  );
}
